import { useEffect, useState } from 'react'
import { X, BookOpen } from 'lucide-react'
import { useHighlightsApi } from './useHighlightsApi'

// Highlights — AI methods documentation.
// Structured chart of every AI/detector method the module ships, organized
// into three categories: ingest-time detectors, search-time enhancers, and
// agent-driven flows. Each entry carries a short purpose blurb + columns
// that mirror the in-chat summary (cost, speed, status, sublayer).
//
// Toggle state for ingest detectors is fetched live from /settings so the
// "current status" column reflects what the deployed worker is actually
// doing — not what was true when the docs were written.

const COLOR = {
  mux:        { bg: 'rgba(251,146,60,0.15)',  fg: '#fb923c' },
  tbtv_ai_kw: { bg: 'rgba(167,139,250,0.15)', fg: '#a78bfa' },
  tbtv_ai_llm:{ bg: 'rgba(34,197,94,0.15)',   fg: '#22c55e' },
  search:     { bg: 'rgba(59,130,246,0.15)',  fg: '#3b82f6' },
  agent:      { bg: 'rgba(244,114,182,0.15)', fg: '#f472b6' },
  foundation: { bg: 'rgba(125,211,252,0.15)', fg: '#7dd3fc' },
}

// All methods enumerated. `toggleKey` lets us bind the live status from
// /settings; absent = always-on or N/A.
const METHODS = {
  ingest: [
    {
      id: 'mux_stt',
      name: 'Mux STT (auto-captions)',
      family: 'foundation',
      purpose: 'Transcribes broadcast audio into WebVTT subtitle cues. Foundation for every downstream detector — none of the other methods work without this.',
      reads: 'Mux asset audio track',
      writes: 'WebVTT track on Mux + transcript_cues + 3 FTS5 shadow tables',
      cost: 'Free (bundled with Mux encoding)',
      speed: '~10× realtime',
      strengths: 'Free, hands-free, accurate on clear announcer audio.',
      weaknesses: 'Proper nouns and player names are routinely mangled (e.g. "Kaczorowski" → "Kazerski"). That is why we layer phonetic search + alias scanning on top.',
      toggleKey: null,
    },
    {
      id: 'mux_fkm',
      name: 'Mux Find Key Moments',
      family: 'mux',
      purpose: 'Multi-modal moment detector — combines crowd-noise spikes, visual activity, and transcript sentiment to identify highlight-worthy timestamps. Returns 0.0–1.0 quality scores.',
      reads: 'The Mux asset itself (audio + visual + transcript)',
      writes: "key_moments rows tagged source='mux'",
      cost: '$0.50 per game (2hr)',
      speed: '~5 minutes per game',
      strengths: 'Multi-modal — sees what we can\'t (visual cues, crowd energy). Sport-agnostic baseline.',
      weaknesses: 'Expensive vs TBTV-AI alternatives. Sport-agnostic means it misses sport-specific signal types. Only ~10 moments per game (capped by max_moments).',
      toggleKey: 'robots_find_key_moments_enabled',
      defaultRecommendation: 'OFF — replaced by TBTV-AI Find Key Moments at ~17× cheaper cost.',
    },
    {
      id: 'mux_chapters',
      name: 'Mux Generate Chapters',
      family: 'mux',
      purpose: 'Splits the broadcast into logical sections (period starts, halftimes, drives) using transcript structure. Powers chapter navigation in the player.',
      reads: 'The Mux asset transcript',
      writes: "chapters rows tagged source='mux'",
      cost: '$0.063 per game',
      speed: '~5 minutes per game',
      strengths: 'Cheap. Mux\'s chapter boundary detection is solid for clearly-structured broadcasts.',
      weaknesses: 'Requires the Mux asset_id be populated in our content row. Falls back if the live→VOD sync hasn\'t completed.',
      toggleKey: 'robots_generate_chapters_enabled',
    },
    {
      id: 'mux_summarize',
      name: 'Mux Summarize',
      family: 'mux',
      purpose: 'Auto-generates a video title + description + tags from the full transcript. Editorial uses this to seed catalog metadata.',
      reads: 'The Mux asset full transcript',
      writes: "video_summaries rows tagged source='mux'",
      cost: '$0.04 per game',
      speed: '~5 minutes per game',
      strengths: 'Cheap. Generic but reliable. Good baseline catalog text.',
      weaknesses: 'Tone is neutral / wire-service. Doesn\'t reflect editorial voice. TBTV-AI Summarize is the editorial-tuned alternative.',
      toggleKey: 'robots_summarize_enabled',
    },
    {
      id: 'tbtv_ai_kw',
      name: 'TBTV-AI keyword sublayer',
      family: 'tbtv_ai_kw',
      purpose: 'FTS5 scan of the transcript for sport-specific vocabulary patterns ("goal", "scores", "back of the net", "power play", "boarding penalty"). Each pattern match runs through a multi-stage filter chain before emission.',
      reads: 'transcript_cues_fts + sport vocab JSON + dropped_moments + Claude verifier',
      writes: "key_moments rows source='tbtv_ai' detector='keyword'",
      cost: 'Free regex + ~$0.001/game for verifier (Step 25b)',
      speed: '<1s scan + ~5–15s verifier per game',
      strengths: 'Deterministic on the regex side. Timestamps always exact (pulled from real cues). High recall on goal calls, saves, fights, penalties. The Step 25b Claude verifier rejects ~60–80% of false positives before insert.',
      weaknesses: 'False positives leak when stat-readings sound like live calls. Mitigation stack: (1) bracket filter (skip [MUSIC]/[BLANK_AUDIO]), (2) past-tense recap regex (Step 23 + 26.12 patterns), (3) multi-keyword cue filter (drop cue if 3+ vocab types match), (4) Claude verifier (Step 25b), (5) 5-min goal cooldown (Step 26.12), (6) exact-cue-text dedup catches replay narration (Step 26.12), (7) per-video drop list (Step 26).',
      toggleKey: 'tbtv_ai_find_key_moments_enabled',
    },
    {
      id: 'tbtv_ai_llm',
      name: 'TBTV-AI LLM sublayer (Claude Haiku 4.5)',
      family: 'tbtv_ai_llm',
      purpose: 'Splits the transcript into ~600-cue chunks (Step 25h) and fires one Claude Haiku 4.5 call per chunk in parallel. The model returns moments grounded to specific cue_id citations; the server derives start_ms / end_ms from those cues (Step 23). RAG context (Step 29) prepends recent editorial drops + approves to every prompt so editorial work compounds across videos.',
      reads: 'Full transcript with [cue_id=N, hh:mm:ss] prefixes + RAG exemplars (5 recent drops + 3 recent approves)',
      writes: "key_moments rows source='tbtv_ai' detector='llm' + grounded_cue_ids JSON",
      cost: '~$0.03 per game (~6 chunks × ~$0.005 each) + ~$0.001 for RAG injection',
      speed: '~15–30 seconds per game (chunks run in parallel)',
      strengths: 'Semantic understanding — catches "what a save by Murphy in the slot" even without specific vocab. Chunked parallel calls land reliably (no more waitUntil-killed-mid-chain failures). RAG injection means every rejection you make on one video reduces similar emissions across the catalog. LLM-first ordering (Step 26.10) gives this layer the budget; KW retries via the hourly cron if needed.',
      weaknesses: 'Slow + paid. Even with cue-citation grounding (Step 23) and goal-evidence validation (Step 25e — drops emissions whose cited cues lack explicit score-change language), the model can still emit moments that get fully filtered to zero in heavy filter chains. Filter stack: (1) Step 23 cue-citation validation (drops unknown cue_ids), (2) Step 25e goal evidence check, (3) Step 26.11 score-only dedup, (4) Step 26 per-video drop list (±3s match against your rejections), (5) Step 26.12 recap patterns.',
      toggleKey: 'tbtv_ai_find_key_moments_enabled',
      sublayerOf: 'tbtv_ai_find_key_moments_enabled',
    },
    {
      id: 'tbtv_ai_chapters',
      name: 'TBTV-AI Generate Chapters',
      family: 'tbtv_ai_llm',
      purpose: 'Claude reads the full transcript and proposes chapter boundaries tuned to the sport (period starts, key drives, narrative arcs).',
      reads: 'Full transcript blob',
      writes: "chapters rows source='tbtv_ai'",
      cost: '~$0.015 per game',
      speed: '~30 seconds per game',
      strengths: 'Sport-aware. Cheaper than Mux chapters. Doesn\'t depend on the Mux asset_id being synced.',
      weaknesses: 'Quality depends on prompt tuning per sport.',
      toggleKey: 'tbtv_ai_generate_chapters_enabled',
    },
    {
      id: 'tbtv_ai_summarize',
      name: 'TBTV-AI Summarize',
      family: 'tbtv_ai_llm',
      purpose: 'Claude generates a title + description + tags in editorial voice with sport-specific framing — alternative to Mux Summarize\'s generic output.',
      reads: 'Full transcript + Mux summary (if present) as context',
      writes: "video_summaries rows source='tbtv_ai'",
      cost: '~$0.015 per game',
      speed: '~20 seconds per game',
      strengths: 'House style. Reflects sport context. Cheaper than Mux Summarize.',
      weaknesses: 'Quality depends on prompt tuning. New videos that haven\'t been spot-checked may need editorial review.',
      toggleKey: 'tbtv_ai_summarize_enabled',
    },
    {
      id: 'claude_alias_scan',
      name: 'Claude Alias Scanner (Step 20)',
      family: 'tbtv_ai_llm',
      purpose: 'One-shot Claude sweep that clusters STT-mangled tokens (Kazerski / Casarowski / Kachorowski) and proposes canonical→alias mappings the editor can approve. Approved aliases auto-expand future searches.',
      reads: 'Full transcript per video',
      writes: 'search_aliases rows proposed_by=claude_haiku, awaiting editor review',
      cost: '~$0.016 per video, one-shot',
      speed: '~30 seconds per video',
      strengths: 'Catalog-wide search recall boost for $0 ongoing cost (aliases run client-side at query time).',
      weaknesses: 'Requires editor review to flip status=proposed → status=active. Quality depends on broadcast-specific vocabulary.',
      toggleKey: 'claude_alias_scan_enabled',
    },
  ],
  search: [
    {
      id: 'fts_exact',
      name: 'FTS5 exact (Porter stem)',
      family: 'search',
      purpose: 'Direct word-stem match on transcript_cues. Returns cues containing the literal query terms (with English stemming so "scoring" matches "scores").',
      cost: 'Free',
      speed: '~5–20ms',
      strengths: 'Fast. Precise. The highest-confidence layer.',
      weaknesses: 'Misses STT mishearings entirely — searching "Kaczorowski" never finds "Kazerski" via this layer.',
    },
    {
      id: 'fts_phonetic',
      name: 'FTS5 phonetic (Double Metaphone)',
      family: 'search',
      purpose: 'Matches cues where the queried word SOUNDS the same as the cue\'s word, even if spelled differently. Crucial for proper nouns mangled by STT.',
      cost: 'Free',
      speed: '~10–30ms',
      strengths: 'Catches "Kazerski" / "Casarowski" / "Kazrowski" when you type "Kaczorowski" — they all share the same DM primary code.',
      weaknesses: 'Numbers and short words match too aggressively. Best used in combination with exact + trigram for ranking.',
    },
    {
      id: 'fts_trigram',
      name: 'FTS5 trigram',
      family: 'search',
      purpose: 'Substring-fuzzy match — splits both query and cue text into 3-character chunks, matches on overlap. Catches typos and partial matches.',
      cost: 'Free',
      speed: '~20–40ms',
      strengths: 'Tolerates misspellings and partial words.',
      weaknesses: 'Lots of noise. Lowest-confidence layer; ranked below exact + phonetic.',
    },
    {
      id: 'alias_expansion',
      name: 'Alias expansion',
      family: 'search',
      purpose: 'Before running FTS, expands each query term using search_aliases — adds known synonyms / nicknames / STT-mishearings so they all match. Combined with phonetic this gives near-perfect proper-noun recall.',
      cost: 'Free at query time',
      speed: '~1ms',
      strengths: 'Catalog-wide recall boost. Editor-curated entries are 100% reliable.',
      weaknesses: 'Empty until either Step 20 scanner runs or editor manually adds entries.',
    },
    {
      id: 'boolean_parser',
      name: 'Boolean AND/OR/NOT (Step 18)',
      family: 'search',
      purpose: 'Parses uppercase AND / OR / NOT operators in the query so editors can build narrow searches: `"Caden Kaczorowski" AND "goal"`, `Murphy AND NOT penalty`, `(A OR B) AND C`.',
      cost: 'Free',
      speed: '<1ms parse + runs as native FTS5 expression',
      strengths: 'Expressive narrowing without UI complexity. Quoted phrases still work (e.g. `"power play"`).',
      weaknesses: 'Requires uppercase to disambiguate from names containing the words "and" / "or". UI cheatsheet (?) icon next to the search box.',
    },
    {
      id: 'km_text_search',
      name: 'KM text search (LIKE)',
      family: 'search',
      purpose: 'In parallel with the cue search, scans key_moments.{title, audible_narrative, visual_narrative, concepts} for the same query terms. Produces the 💫 KM matches above the cue list.',
      cost: 'Free',
      speed: '~50–150ms',
      strengths: 'Surfaces matches that hit the AI-generated title/narrative even when the underlying cue text doesn\'t.',
      weaknesses: 'Catalog-wide LIKE is O(N) — fine at our scale, less so once we hit ~10k KM rows.',
    },
    {
      id: 'smart_expand',
      name: 'Claude Smart Expand (Step 19)',
      family: 'tbtv_ai_llm',
      purpose: 'Per-query opt-in: Claude reads the user\'s query and proposes 6–10 alternative phrasings + 3–5 likely STT mishearings. These get OR\'d into the FTS query for a one-shot recall boost. Cached in KV for 30 days per query.',
      cost: '~$0.0008 per uncached query',
      speed: '~600–800ms cold; instant cached',
      strengths: 'Catches phrasings the editor didn\'t think to type. Cache amortizes cost across repeat searches.',
      weaknesses: 'Costs money per query — editor opts in via the 🪄 Smart Expand button.',
      toggleKey: 'tbtv_ai_search_rewrite_mode',
    },
    {
      id: 'intent_decompose',
      name: 'Llama 3.3 70B Intent Decomposer (Step 21 Phase C)',
      family: 'tbtv_ai_llm',
      purpose: 'First stage of the agentic search pipeline. Takes the natural-language query and returns structured intent — entities (resolved against rosters when available), event_types, filters (date/team/league), ranking intent, semantic hints.',
      cost: '~$0.00025 per call (60% cache hit on identical queries within 1hr)',
      speed: '~500ms cold; instant cached',
      strengths: 'Unblocks downstream stages — recall + rerank both consume the structured intent.',
      weaknesses: 'Failure mode = Llama emits a structurally wrong intent (e.g. drops the entity name). Falls back to raw query if validation fails.',
    },
    {
      id: 'workers_ai_bge',
      name: 'Workers AI BGE embeddings',
      family: 'search',
      purpose: 'Embeds cues, key moments, and chapters at ingest into 1024-dim vectors stored in Cloudflare Vectorize. Runs again on the search query at runtime. Enables semantic similarity search beyond keyword overlap.',
      cost: 'Free (Cloudflare Workers AI free tier; ~100k req/day)',
      speed: '~50ms per embed call',
      strengths: 'Free. Native edge. Catches conceptual matches FTS misses (e.g. "puck went in" matching "goal").',
      weaknesses: 'Quality slightly below paid models like Voyage embed-3-large. Rerank compensates.',
    },
    {
      id: 'vectorize_search',
      name: 'Vectorize semantic search (Step 21 Phase A)',
      family: 'search',
      purpose: 'Cosine similarity query against the embedded cue/KM/chapter vectors. Returns top-K nearest neighbors with similarity scores.',
      cost: '~$0.04/M vectors/mo storage',
      speed: '~30ms',
      strengths: 'Conceptually-relevant matches without exact-word overlap. Complements FTS rather than replacing.',
      weaknesses: 'Recall is broad — needs the reranker stage to narrow down.',
    },
    {
      id: 'llm_reranker',
      name: 'LLM Reranker (Step 21 Phase C)',
      family: 'tbtv_ai_llm',
      purpose: 'Takes the top 200 candidates from the recall stage + the structured intent, and asks an LLM to rerank them into the top 20 with one-sentence rationales. Configurable: Llama 3.3 70B (default, cheaper) or Claude Haiku 4.5 (slower cold start, higher quality).',
      cost: '~$0.0024 (Llama) or ~$0.005 (Haiku) per call. 60% cache hit on identical query+candidate set within 24hr',
      speed: '~9s cold (Haiku), instant cached',
      strengths: 'Single biggest precision boost in the agentic pipeline. Rationales help editorial trust + debug.',
      weaknesses: 'Slow cold start. Editor opts in via the 🧠 Agentic button.',
      toggleKey: 'llm_reranker_model',
    },
  ],
  agent: [
    {
      id: 'reel_agent',
      name: 'Reel-Building Agent (Step 21 Phase D — planned)',
      family: 'agent',
      purpose: 'Editor describes a reel intent in natural language ("build me a 90s reel of Casaroski goals from the championship"). Agent runs the full search pipeline, selects best clips, applies diversity + duration constraints, and pre-fills the reel cart with rationale per clip.',
      cost: '~$0.030 (Sonnet 4.6) per invocation',
      speed: '~10–15 seconds',
      strengths: 'Collapses the "search → curate → cart → trim → save" workflow from ~10 min to ~1 min per reel.',
      weaknesses: 'Not yet built. Designed and documented in Step 21 Phase D.',
      status: 'planned',
    },
  ],
  feedback: [
    {
      id: 'drop_list',
      name: 'Per-video drop list (Step 26)',
      family: 'foundation',
      purpose: 'Every ❌ Reject from KeyMomentsExplorer writes a row to dropped_moments AND deletes the offending key_moments row. On future runs of the SAME video, candidates matching ±3s + normalized title get filtered before insert. Editorial work becomes permanent improvement on that video without code changes.',
      reads: 'dropped_moments table',
      writes: 'dropped_moments rows + deletes matched key_moments',
      cost: 'Free',
      speed: 'Insert-time check (~1ms per candidate)',
      strengths: 'Same-video memory is surgical and permanent. Survives prompt changes, model swaps, re-applies. Per-row Undo within the toast window.',
      weaknesses: 'Only filters the same video — cross-video pattern learning is what RAG injection does (see below).',
    },
    {
      id: 'reject_taxonomy',
      name: 'Reject reason taxonomy (Step 26.5)',
      family: 'foundation',
      purpose: 'Every reject is categorized: hallucinated / recap / wrong_type / marginal / wrong_window / quality / other. The category determines whether the rejection feeds the RAG drop pool downstream.',
      reads: '—',
      writes: 'dropped_moments.reason_category column',
      cost: 'Free',
      speed: 'N/A',
      strengths: 'ONLY hallucinated + recap reasons feed Step 29 RAG. Other categories are bookkeeping. Editor knows at reject time whether the label compounds globally or stays per-video.',
      weaknesses: 'Editor has to pick the right reason. Wrong category = wasted label.',
    },
    {
      id: 'approve_list',
      name: 'Approve list (Step 27)',
      family: 'foundation',
      purpose: 'Every ✓ Approve writes to approved_moments. Doubles as (a) positive RAG exemplars (3 most recent shown per LLM call) and (b) gold-set positives for the eval harness.',
      reads: '—',
      writes: 'approved_moments rows',
      cost: 'Free',
      speed: 'N/A',
      strengths: 'Tells the model what GOOD looks like, not just what to avoid. Required for the eval harness to compute precision/recall.',
      weaknesses: 'Easy to forget — most editors over-reject and under-approve. Aim for roughly balanced label counts to keep RAG context useful.',
    },
    {
      id: 'rag_injection',
      name: 'RAG exemplar injection (Step 29)',
      family: 'tbtv_ai_llm',
      purpose: 'Before every TBTV-AI LLM call, fetches the 5 most-recent hallucinated/recap drops + 3 most-recent approves (catalog-wide, excluding current tbtv_id) and prepends them to the user message. The model sees "do not emit similar to X" + "this is what good looks like" alongside the transcript.',
      reads: 'dropped_moments + approved_moments',
      writes: '—',
      cost: '~$0.001 per game (~500 extra input tokens × chunks)',
      speed: '+~1s per game (one extra D1 query)',
      strengths: 'Editorial decisions on one video reduce similar emissions across the catalog. No code changes needed — every reject compounds. Visible in SettingsEditor → ✨ RAG exemplars panel with expandable "what the model sees".',
      weaknesses: 'Probabilistic (not deterministic like the per-video drop list). 5 + 3 limits mean older labels rotate out — bump tbtv_ai_rag_max_drops if you want a wider exemplar pool. Only hallucinated + recap reasons participate.',
      toggleKey: 'tbtv_ai_rag_exemplars_enabled',
    },
    {
      id: 'eval_harness',
      name: 'Eval harness (Step 27)',
      family: 'foundation',
      purpose: 'Grades the live TBTV-AI output against your approve list (positives) + drop list (negatives) using a ±10s greedy matcher. Reports precision / recall / F1 per video and overall. Run via ▶ Run eval in SettingsEditor — also viewable as a history.',
      reads: 'key_moments + approved_moments + dropped_moments',
      writes: 'eval_runs rows + per-video tallies',
      cost: 'Free',
      speed: '~5–10 seconds catalog-wide',
      strengths: 'Single trusted metric for "is the model getting better?" Catches regressions when prompt / vocab / filters change. Required before any structural model swap (Step 31 fine-tuning).',
      weaknesses: 'Only as good as the gold labels. Garbage labels in → garbage scores out. Re-grade after every meaningful labeling session.',
    },
    {
      id: 'job_recovery',
      name: 'Job Recovery + Idempotency (Step 26.13)',
      family: 'foundation',
      purpose: 'recordRobotsJob refuses to insert a parallel row when a non-terminal (pending/processing) job already exists for the same (tbtv_id, job_type) — double-clicks no longer double-charge or produce phantom rows. SettingsEditor → Job recovery panel lists stuck (≥3 min old) + recently errored jobs with bulk Unstick / Re-run actions. Re-run fires one HTTP request per (job_type, tbtv_id) pair so each gets its own ctx.waitUntil budget.',
      reads: 'robots_jobs',
      writes: 'robots_jobs status updates',
      cost: 'Free',
      speed: 'Real-time UI',
      strengths: 'Recovers from ctx.waitUntil cancellations without manual SQL. Idempotency claim prevents double-fire from double-clicks. Serial Re-run prevents the batch-budget-exhaustion bug that landed earlier.',
      weaknesses: 'Stuck-job detection threshold is 3 minutes — short-running jobs that finish in 60s won\'t show up here even if they get retried by the cron later.',
    },
  ],
}

const SECTION_META = {
  ingest:   { title: 'Ingest-time detectors', blurb: 'Run once when a video is captioned. Write rows to key_moments / chapters / video_summaries.' },
  feedback: { title: 'Editorial feedback loop', blurb: 'How your ❌ rejects and ✓ approvals compound into permanent improvements across the catalog — per-video drop list, RAG injection into the next LLM call, eval harness scoring, and the job recovery surface.' },
  search:   { title: 'Search-time enhancers', blurb: 'Run per query. Don\'t persist anything — read existing data + improve recall/precision.' },
  agent:    { title: 'Agent-driven workflows', blurb: 'Multi-step pipelines invoked on-demand by an editor.' },
}

function FamilyBadge({ family }) {
  const labels = {
    foundation:  { label: 'Foundation' },
    mux:         { label: 'Mux Robots' },
    tbtv_ai_kw:  { label: 'TBTV-AI · keyword' },
    tbtv_ai_llm: { label: 'TBTV-AI · LLM' },
    search:      { label: 'Search engine' },
    agent:       { label: 'Agentic' },
  }
  const c = COLOR[family]
  const l = labels[family]?.label || family
  return (
    <span style={{
      fontSize: 10, padding: '1px 7px', borderRadius: 10,
      background: c.bg, color: c.fg, fontWeight: 700, letterSpacing: 0.3, whiteSpace: 'nowrap',
    }}>{l}</span>
  )
}

function StatusBadge({ status, toggleKey, settings }) {
  if (status === 'planned') {
    return <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, background: '#1f2937', color: '#888', fontWeight: 600 }}>planned</span>
  }
  if (!toggleKey || !settings) {
    return <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, background: 'rgba(34,197,94,0.15)', color: '#22c55e', fontWeight: 600 }}>always on</span>
  }
  const val = settings[toggleKey]
  if (val == null) return <span style={{ fontSize: 10, color: '#666' }}>—</span>
  // Boolean-ish settings: 'true' / 'false' = auto-run on caption ingest.
  // Both states still allow manual runs from the Run AI modal.
  if (val === 'true') return <span title="Auto-runs after captions become ready. Manual runs also available." style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, background: 'rgba(34,197,94,0.15)', color: '#22c55e', fontWeight: 600, cursor: 'help' }}>AUTO</span>
  if (val === 'false') return <span title="Does NOT auto-run on caption ingest. Still available manually via the Run AI modal." style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, background: 'rgba(252,165,165,0.10)', color: '#fca5a5', fontWeight: 600, cursor: 'help' }}>MANUAL</span>
  // Anything else (e.g. model name): display verbatim.
  return <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, background: 'rgba(125,211,252,0.10)', color: '#7dd3fc', fontWeight: 600 }}>{val}</span>
}

function MethodCard({ m, settings }) {
  return (
    <div style={{
      border: '1px solid #2a2a2a', borderRadius: 6, padding: '12px 14px',
      display: 'flex', flexDirection: 'column', gap: 8,
      background: 'rgba(255,255,255,0.015)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#dbe5f5' }}>{m.name}</div>
        <FamilyBadge family={m.family} />
        <div style={{ flex: 1 }} />
        <StatusBadge status={m.status} toggleKey={m.toggleKey} settings={settings} />
      </div>
      <div style={{ fontSize: 13, color: '#bbb', lineHeight: 1.5 }}>{m.purpose}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 10, rowGap: 3, fontSize: 12 }}>
        {m.reads && (<>
          <span style={{ color: '#777' }}>Reads:</span>
          <span style={{ color: '#aaa' }}>{m.reads}</span>
        </>)}
        {m.writes && (<>
          <span style={{ color: '#777' }}>Writes:</span>
          <span style={{ color: '#aaa' }}>{m.writes}</span>
        </>)}
        <span style={{ color: '#777' }}>Cost:</span>
        <span style={{ color: '#aaa' }}>{m.cost}</span>
        <span style={{ color: '#777' }}>Speed:</span>
        <span style={{ color: '#aaa' }}>{m.speed}</span>
      </div>
      {m.strengths && (
        <div style={{ fontSize: 12, color: '#a3e4b5', lineHeight: 1.5 }}>
          <strong style={{ color: '#86efac' }}>Strengths:</strong> {m.strengths}
        </div>
      )}
      {m.weaknesses && (
        <div style={{ fontSize: 12, color: '#f5a3a3', lineHeight: 1.5 }}>
          <strong style={{ color: '#fca5a5' }}>Weaknesses:</strong> {m.weaknesses}
        </div>
      )}
      {m.defaultRecommendation && (
        <div style={{ fontSize: 11, color: '#7dd3fc', fontStyle: 'italic' }}>
          {m.defaultRecommendation}
        </div>
      )}
    </div>
  )
}

export default function AiDocsModal({ onClose }) {
  const api = useHighlightsApi()
  const [settings, setSettings] = useState(null)

  // Pull live toggle state so the ON/OFF badges reflect what's deployed.
  useEffect(() => {
    let cancelled = false
    api.listSettings().then(r => {
      if (cancelled) return
      const map = {}
      for (const row of (r?.settings || [])) map[row.key] = row.value
      setSettings(map)
    }).catch(() => { /* graceful — show "—" badges */ })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.() }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
      }}>
      <div style={{
        background: '#0e1015', border: '1px solid #2a2a2a', borderRadius: 8,
        width: 'min(960px, 96vw)', maxHeight: '92vh', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #2a2a2a', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <BookOpen size={18} style={{ color: '#7dd3fc' }} />
            <div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>About the AI methods</div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                Every detector, enhancer, and agent we ship — what each does, what it costs, what it's good at, and what it isn't.
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px' }}>
          {/* Mental model graphic */}
          <div style={{
            background: 'rgba(125,211,252,0.04)', border: '1px solid #1f2a40',
            padding: '12px 14px', borderRadius: 6, marginBottom: 20,
            fontSize: 12, color: '#bbb', lineHeight: 1.5,
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#dbe5f5', marginBottom: 6 }}>How they relate</div>
            <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: 11, color: '#aaa', whiteSpace: 'pre' }}>
{`                  Mux STT  (free, foundational)
                       │
                       ▼
                transcript_cues
                       │
     ┌─────────┬───────┼───────┬─────────┐
     ▼         ▼       ▼       ▼         ▼
  Mux FKM   Mux Chap Mux Sum  TBTV-AI  Alias scan
                              (kw + LLM)
                       │
                       │  ◀── 🔁 RAG context from
                       │       editorial drops + approves
                       │       (Step 29 — every reject
                       │        compounds across videos)
       ───────────────┴────────────────
                       │
                       ▼
   key_moments / chapters / video_summaries
                       │
       ┌───────────────┴───────────────┐
       ▼                               ▼
  ❌ Reject  ──▶  drop list ──▶ filtered on next run (per-video)
  ✓ Approve ──▶  RAG pool   ──▶ shown to LLM on every video
                                Eval harness ──▶ precision/recall
                       │
                       ▼  (at search time)
   FTS5 × 3 layers  +  Vectorize  +  KM text search
                       │
       ┌───────────────┼────────────────┐
       ▼               ▼                ▼
  Boolean AST    Smart Expand    Agentic pipeline
                                 (intent → recall → rerank)`}
            </pre>
          </div>

          {/* Four sections of methods */}
          {['ingest', 'feedback', 'search', 'agent'].map(section => (
            <div key={section} style={{ marginBottom: 24 }}>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#dbe5f5' }}>{SECTION_META[section].title}</div>
                <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{SECTION_META[section].blurb}</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
                {METHODS[section].map(m => (
                  <MethodCard key={m.id} m={m} settings={settings} />
                ))}
              </div>
            </div>
          ))}

          {/* Cue length + padding reference */}
          <div style={{ marginTop: 8, marginBottom: 24 }}>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#dbe5f5' }}>Cue length &amp; padding — how each detector chooses clip bounds</div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                The trim window for any segment built from a key moment is
                <code style={{ background: '#1f2937', padding: '0 4px', borderRadius: 3, margin: '0 4px' }}>[moment.start_ms − pre_ms, moment.end_ms + post_ms]</code>.
                Each detector sets <code style={{ background: '#1f2937', padding: '0 4px', borderRadius: 3 }}>start_ms</code> / <code style={{ background: '#1f2937', padding: '0 4px', borderRadius: 3 }}>end_ms</code> differently, which is why clip durations vary.
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
              <div style={{
                border: '1px solid #2a2a2a', borderRadius: 6, padding: '10px 14px',
                background: 'rgba(251,146,60,0.04)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, background: 'rgba(251,146,60,0.15)', color: '#fb923c', fontWeight: 700, letterSpacing: 0.3 }}>Mux Find Key Moments</span>
                </div>
                <div style={{ fontSize: 12, color: '#bbb', lineHeight: 1.5 }}>
                  <strong style={{ color: '#dbe5f5' }}>start_ms / end_ms:</strong> set by Mux's own multi-modal pipeline. Typical span 10–30s. We don't control this — whatever Mux returns is what we store.
                </div>
              </div>

              <div style={{
                border: '1px solid #2a2a2a', borderRadius: 6, padding: '10px 14px',
                background: 'rgba(167,139,250,0.04)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, background: 'rgba(167,139,250,0.15)', color: '#a78bfa', fontWeight: 700, letterSpacing: 0.3 }}>TBTV-AI · keyword</span>
                </div>
                <div style={{ fontSize: 12, color: '#bbb', lineHeight: 1.5 }}>
                  <strong style={{ color: '#dbe5f5' }}>start_ms:</strong> the matched cue's start_ms (always accurate — pulled from <code>transcript_cues</code>).
                  <br />
                  <strong style={{ color: '#dbe5f5' }}>end_ms:</strong> <code>max(cue.end_ms, cue.start_ms + 8000)</code> — i.e. the cue's actual end OR 8 seconds, whichever is later. Gives every keyword moment a minimum 8s visible window for context.
                  <br />
                  <em style={{ color: '#888' }}>Typical span 8–15s.</em>
                </div>
              </div>

              <div style={{
                border: '1px solid #2a2a2a', borderRadius: 6, padding: '10px 14px',
                background: 'rgba(34,197,94,0.04)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, background: 'rgba(34,197,94,0.15)', color: '#22c55e', fontWeight: 700, letterSpacing: 0.3 }}>TBTV-AI · LLM</span>
                </div>
                <div style={{ fontSize: 12, color: '#bbb', lineHeight: 1.5 }}>
                  <strong style={{ color: '#dbe5f5' }}>start_ms:</strong> the FIRST cited cue's start_ms (Step 23 cue-citation grounding — LLM emits cue_ids, server derives the timestamp).
                  <br />
                  <strong style={{ color: '#dbe5f5' }}>end_ms:</strong> the LAST cited cue's end_ms.
                  <br />
                  <strong style={{ color: '#dbe5f5' }}>Cue-span cap:</strong> bounded by the <code>tbtv_ai_llm_cue_span_mode</code> setting (Settings → Cue span). Modes: <strong style={{ color: '#3b82f6' }}>tight</strong> = max 3 cues, <strong style={{ color: '#22c55e' }}>normal</strong> = max 5, <strong style={{ color: '#c4b5fd' }}>loose</strong> = max 10. Enforced both in the prompt AND server-side (LLM-cited ranges that exceed the cap get clamped to start + cap cues).
                  <br />
                  <em style={{ color: '#888' }}>Typical span: 10–25s (tight) / 15–40s (normal) / 30–80s (loose). Varies per-moment based on LLM's read of "where this play starts and ends."</em>
                </div>
              </div>

              <div style={{
                border: '1px solid #2a2a2a', borderRadius: 6, padding: '10px 14px',
                background: 'rgba(125,211,252,0.04)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, background: 'rgba(125,211,252,0.15)', color: '#7dd3fc', fontWeight: 700, letterSpacing: 0.3 }}>Reel-build padding</span>
                </div>
                <div style={{ fontSize: 12, color: '#bbb', lineHeight: 1.5 }}>
                  When a moment is added to a reel, the clip window expands to <code>[start_ms − pre_ms, end_ms + post_ms]</code>, default 10s + 10s. Editor controls both via the <strong>pre / post</strong> inputs in the Reel Builder.
                  <br />
                  Additionally, the <strong>clip body</strong> selector in the Reel Builder (<em>Full / Tight / Short / Medium / Long / Loose</em>) caps the moment body itself BEFORE padding — useful for tightening 50s LLM spans down to 10s without re-running the detector. Per-reel only; doesn't modify the underlying <code>key_moments</code> row.
                </div>
              </div>

              <div style={{
                border: '1px solid #2a2a2a', borderRadius: 6, padding: '10px 14px',
                background: 'rgba(244,114,182,0.04)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, background: 'rgba(244,114,182,0.15)', color: '#f472b6', fontWeight: 700, letterSpacing: 0.3 }}>Per-segment override (Reel Editor)</span>
                </div>
                <div style={{ fontSize: 12, color: '#bbb', lineHeight: 1.5 }}>
                  Once a reel exists, the Reel Editor lets you trim each segment individually — drag the In / Out handles on the trim range slider, use the time inputs, or click <strong>Set In</strong> / <strong>Set Out</strong> from the playhead position. The zoom slider above the trim track adjusts per-pixel resolution so sub-second adjustments are possible on long sources.
                </div>
              </div>
            </div>
          </div>

          {/* Footer note */}
          <div style={{
            marginTop: 8, padding: '10px 12px', borderRadius: 6,
            background: 'rgba(255,255,255,0.02)', border: '1px solid #1f1f1f',
            fontSize: 11, color: '#888',
          }}>
            AUTO-RUN / MANUAL-ONLY badges reflect whether the detector auto-runs on caption ingest for new videos. They do NOT gate the Run AI modal — every detector can be fired manually from the modal regardless of its auto-run setting. Adjust auto-run defaults in <strong>Settings</strong> (gear icon, top-right).
          </div>
        </div>
      </div>
    </div>
  )
}
